import { ENDPOINTS, QUERY_CACHE } from '@/model/endpoints';
import {useMutation, useQuery} from "@tanstack/react-query";
import {type RegistrationConfirmation, type RegistrationForm, type RegistrationRequest} from '@iziping/common';
import { apiClient } from '@iziping/common';
import {type ApiError} from '@iziping/common';
import type {AxiosResponse} from "axios";
import {type PreRegistration} from '@iziping/common';

export const useGetPreRegistrations = (tournamentId: number) => {
    return useQuery<PreRegistration[], Error>({
        queryKey: [QUERY_CACHE.PRE_REGISTRATIONS_LIST, tournamentId],
        queryFn: async () => {
            const response = await apiClient.get<PreRegistration[]>(ENDPOINTS.preRegistrationsList(tournamentId));
            return response.data;
        },
        enabled: !!tournamentId,
    });
};

const fetchRegistrationData = async (uuid: string, registrationId?: string): Promise<RegistrationForm> => {
    const endpoint = registrationId
        ? ENDPOINTS.provisionalEditRegistration(uuid, registrationId)  // /registration/:uuid/edit/:registrationId
        : ENDPOINTS.provisionalRegistration(uuid);                     // /registration/:uuid

    try {
        const response = await apiClient.get<RegistrationForm>(endpoint);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 404) {
            throw new Error("Lien invalide ou expiré.");
        }
        throw new Error("Erreur lors de la récupération des données.");
    }
};

export const useRegistrationData = (uuid?: string, registrationId?: string) => {
    return useQuery<RegistrationForm, Error>({
        queryKey: [QUERY_CACHE.PRE_REGISTRATIONS, uuid, registrationId],
        queryFn: async () => {
            if (!uuid) throw new Error("UUID manquant pour récupérer la registration");
            return fetchRegistrationData(uuid, registrationId);
        },
        enabled: !!uuid, // n'appelle la query que si uuid est défini
        retry: false
    });
};


interface PreRegistrationParams {
    tournamentUUID: string;
    registrationUUID?: string;
    request: RegistrationRequest;
}

export const useCreatePreRegistration = () => {
    return useMutation<RegistrationForm, ApiError, any>({
        mutationKey: [QUERY_CACHE.CREATE_PRE_REGISTRATION],
        mutationFn: async (params: PreRegistrationParams) => {
            const response = await apiClient.post<RegistrationForm>(ENDPOINTS.provisionalRegistration(params.tournamentUUID), params.request);
            return response.data;
        },
    });
}

export const useUpdatePreRegistration = () => {
    return useMutation<RegistrationForm, ApiError, any>({
        mutationKey: [QUERY_CACHE.EDIT_PRE_REGISTRATIONS],
        mutationFn: async (params: PreRegistrationParams) => {
            const response = await apiClient.put<RegistrationForm>(ENDPOINTS.provisionalEditRegistration(params.tournamentUUID, params.registrationUUID!), params.request);
            return response.data;
        },
    });
}


export const useRegistrationConfirmation = (uuid: string) => {
    return useQuery<RegistrationConfirmation>({
        queryKey: [QUERY_CACHE.REGISTRATION_CONFIRMATION, uuid],
        queryFn: async () => {
            const response = await apiClient.get<RegistrationConfirmation>(ENDPOINTS.registrationConfirmation(uuid));
            return response.data;
        },
        enabled: true,
    });
};

interface RemoveRegistrationParams {
    id: string;
}

export const useDeleteRegistration = () => {
    return useMutation<void, ApiError, RemoveRegistrationParams>({
        mutationKey: [QUERY_CACHE.DELETE_REGISTRATION],
        mutationFn: async (params) => {
            await apiClient.delete(ENDPOINTS.deleteRegistration(params.id)
            );
        },
    });
};

export const useConvertPreRegistrations = () => {
    return useMutation<void, ApiError, { tournamentId: number }>({
        mutationKey: [QUERY_CACHE.CONVERT_PRE_REGISTRATIONS],
        mutationFn: async ({tournamentId}) => {
            await apiClient.post(ENDPOINTS.convertPreRegistration(tournamentId));
        },
    });
};

export const useExportPreRegistrations = () => {
    return useMutation<AxiosResponse<Blob>, ApiError, { tournamentId: number }>({
        mutationKey: [QUERY_CACHE.EXPORT_PRE_REGISTRATIONS],
        mutationFn: async ({tournamentId}) => {
            return await apiClient.get(ENDPOINTS.exportPreRegistrations(tournamentId), {
                responseType: 'blob',
                headers: {Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'},
            });
        },
    });
};


